"use client";

import { c as _c } from "react/compiler-runtime";
import { useCarePlanContext } from "../care-plan";
import { CarePlanEditorLayout } from "../care-plan-editor-layout";
import { CarePlanPreview } from "../care-plan-preview";
import { CycleProgressList } from "../cycle-progress-list";
import { DecisionStep } from "../decision/decision-step";
import { StepCard } from "../step-card";
import { type CarePlanStep, StepType, isLinkedMessage } from "../types";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DateConnector } from "@/components/care-plan/date-connector";
import { CarePlanTagDisplay } from "@/components/care-plan/tag-display";
import { Card } from "@/components/ui";
import { formatDate, formatToHumanReadable } from "@/lib/format-data";
import { AuditTrailTimeline } from "./audit-trail-timeline";
import { DiffContext, type DiffContextType } from "./diff-context";
import { DiffStepDetail } from "./diff-step-detail";
import { DiffStepWrapper } from "./diff-step-wrapper";
import { reconstructAtRevision } from "./reconstruct";
import { type CarePlanSnapshot, type FieldChange } from "./types";
import { useGroupedLogs } from "./use-grouped-logs";
import { carePlanQueryOptions } from "@/api/provider/patient/care-plan-query";
import { carePlanTagQueryOptions } from "@/api/provider/settings/care-plan-tag-query";
import { patientQueryOptions } from "@/api/provider/user/patient/patient-query";
interface AuditTrailSnapshotProps {
  carePlanId: string;
  patientId: string;
}

/**
 * Right panel: renders the care plan as it looked at the selected revision.
 *
 * Composition layers:
 * 1. DiffContext.Provider — feeds diff annotations to DiffStepWrapper + DiffStepDetail
 * 2. CarePlan provider — feeds step data + lookups (no setSteps = read-only)
 * 3. DiffStepWrapper — colored ring + badge around unmodified StepCard
 * 4. DiffStepPopover (composed directly with Popover + DiffStepDetail)
 *    — explicit variant: renders diff-aware badges instead of default StepDetail
 */
export function AuditTrailSnapshot(t0) {
  const $ = _c(51);
  const {
    carePlanId,
    patientId
  } = t0;
  const [selectedIndex, setSelectedIndex] = useState(-1);
  let t1;
  if ($[0] !== carePlanId) {
    t1 = carePlanQueryOptions.logs(carePlanId);
    $[0] = carePlanId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = {
      ...t1
    };
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const {
    data: t3
  } = useQuery(t2);
  let t4;
  if ($[4] !== t3) {
    t4 = t3 === undefined ? [] : t3;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const logs = t4;
  let t5;
  if ($[6] !== patientId) {
    t5 = patientQueryOptions.detail(patientId);
    $[6] = patientId;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t5) {
    t6 = {
      ...t5
    };
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const {
    data: patient
  } = useQuery(t6);
  let t7;
  if ($[10] !== patient) {
    t7 = patient ? `${patient.firstName} ${patient.lastName}`.trim() : null;
    $[10] = patient;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const patientName = t7;
  let t8;
  if ($[12] !== logs) {
    t8 = logs ?? [];
    $[12] = logs;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const groups = useGroupedLogs(t8);
  const groupCount = groups.length;
  const safeIndex = selectedIndex < 0 || selectedIndex >= groupCount ? Math.max(0, groupCount - 1) : selectedIndex;
  let t9;
  bb0: {
    if (groups.length === 0) {
      t9 = null;
      break bb0;
    }
    let t10;
    if ($[14] !== groups || $[15] !== safeIndex) {
      t10 = reconstructAtRevision(groups, safeIndex);
      $[14] = groups;
      $[15] = safeIndex;
      $[16] = t10;
    } else {
      t10 = $[16];
    }
    t9 = t10;
  }
  const revision = t9;
  let t10;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = new Map();
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  const stepRefs = useRef(t10);
  const hasScrolled = useRef(false);
  let t11;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = id => el => {
      if (el) {
        stepRefs.current.set(id, el);
      } else {
        stepRefs.current.delete(id);
      }
    };
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  const setStepRef = t11;
  let t12;
  bb1: {
    if (!revision) {
      t12 = null;
      break bb1;
    }
    t12 = revision.snapshot;
  }
  const snapshot = t12;
  let t13;
  if ($[19] !== snapshot?.displaySteps) {
    t13 = snapshot?.displaySteps.filter(_temp);
    $[19] = snapshot?.displaySteps;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  const timelineSteps = t13;
  let t14;
  if ($[21] !== snapshot?.cycles) {
    t14 = snapshot?.cycles ?? [];
    $[21] = snapshot?.cycles;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  const cycles = t14;
  let t15;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = () => {
      hasScrolled.current = false;
    };
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  let t16;
  if ($[24] !== snapshot) {
    t16 = [snapshot];
    $[24] = snapshot;
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  useEffect(t15, t16);
  let t17;
  if ($[26] !== revision?.annotations) {
    t17 = revision?.annotations ?? new Map();
    $[26] = revision?.annotations;
    $[27] = t17;
  } else {
    t17 = $[27];
  }
  let t18;
  if ($[28] !== revision?.carePlanChanges) {
    t18 = revision?.carePlanChanges ?? [];
    $[28] = revision?.carePlanChanges;
    $[29] = t18;
  } else {
    t18 = $[29];
  }
  let t19;
  if ($[30] !== t17 || $[31] !== t18) {
    t19 = {
      annotations: t17,
      carePlanChanges: t18
    };
    $[30] = t17;
    $[31] = t18;
    $[32] = t19;
  } else {
    t19 = $[32];
  }
  const diff = t19;
  let t20;
  if ($[33] !== diff.annotations || $[34] !== timelineSteps) {
    t20 = () => {
      if (hasScrolled.current) {
        return;
      }
      const firstChanged = timelineSteps?.find(s_0 => {
        const annotation = diff.annotations.get(s_0.id);
        return annotation && annotation.status !== "unchanged";
      });
      if (!firstChanged) {
        return;
      }
      const el_0 = stepRefs.current.get(firstChanged.id);
      if (el_0) {
        el_0.scrollIntoView({
          behavior: "smooth",
          block: "center"
        });
        hasScrolled.current = true;
      }
    };
    $[33] = diff.annotations;
    $[34] = timelineSteps;
    $[35] = t20;
  } else {
    t20 = $[35];
  }
  let t21;
  if ($[36] !== diff.annotations || $[37] !== snapshot || $[38] !== timelineSteps) {
    t21 = [timelineSteps, diff.annotations, snapshot];
    $[36] = diff.annotations;
    $[37] = snapshot;
    $[38] = timelineSteps;
    $[39] = t21;
  } else {
    t21 = $[39];
  }
  useEffect(t20, t21);
  let t22;
  if ($[40] !== cycles || $[41] !== diff.carePlanChanges || $[42] !== groups || $[43] !== patientName || $[44] !== safeIndex || $[45] !== snapshot || $[46] !== timelineSteps) {
    t22 = timelineSteps && timelineSteps.length > 0 ? <CarePlanPreview steps={snapshot?.displaySteps ?? []} orientation="vertical" enableMessages={false} cycles={cycles}><CarePlanEditorLayout rightPanel={<DiffStepDetail />} leftPanel={<div className="flex flex-col gap-3 max-w-xs w-full pointer-events-auto max-h-[calc(100%-5rem)]">{snapshot && <div className="shrink-0 bg-background/70 backdrop-blur-sm rounded-lg shadow-md"><SnapshotHeader snapshot={snapshot} carePlanChanges={diff.carePlanChanges} /></div>}{cycles.length > 0 && <Card className="shadow-md bg-background/70 backdrop-blur-sm shrink-0 max-h-48 overflow-y-auto"><CycleProgressList /></Card>}<Card className="shadow-md bg-background/70 backdrop-blur-sm overflow-y-auto flex-1 min-h-0"><AuditTrailTimeline groups={groups} selectedIndex={safeIndex} onSelect={setSelectedIndex} patientName={patientName} /></Card></div>}><SnapshotSteps setStepRef={setStepRef} /></CarePlanEditorLayout></CarePlanPreview> : <div className="flex flex-col w-full overflow-hidden">{snapshot && <SnapshotHeader snapshot={snapshot} carePlanChanges={diff.carePlanChanges} />}<p className="mt-6 text-center text-body text-foreground-secondary">No steps at this revision.</p></div>;
    $[40] = cycles;
    $[41] = diff.carePlanChanges;
    $[42] = groups;
    $[43] = patientName;
    $[44] = safeIndex;
    $[45] = snapshot;
    $[46] = timelineSteps;
    $[47] = t22;
  } else {
    t22 = $[47];
  }
  let t23;
  if ($[48] !== diff || $[49] !== t22) {
    t23 = <DiffContext.Provider value={diff}>{t22}</DiffContext.Provider>;
    $[48] = diff;
    $[49] = t22;
    $[50] = t23;
  } else {
    t23 = $[50];
  }
  return t23;
}

/**
 * Top-level step list, rendered inside CarePlanPreview so it can read
 * `visibleSteps` from the CarePlan context. That gives us the same
 * "current pass vs all passes" filtering the live editor uses, driven by
 * the toggle in CycleProgressList — preserving real edits that happened
 * on pass 2+ rather than blindly hiding them.
 */
function _temp(s) {
  return !isLinkedMessage(s);
}
function SnapshotSteps(t0) {
  const $ = _c(9);
  const {
    setStepRef
  } = t0;
  const {
    state
  } = useCarePlanContext();
  let t1;
  let t2;
  if ($[0] !== setStepRef || $[1] !== state.visibleSteps) {
    const steps = state.visibleSteps.filter(_temp2);
    t1 = "flex flex-col items-center";
    let t3;
    if ($[4] !== setStepRef) {
      t3 = (step, i) => <SnapshotStep key={step.id} step={step} showConnector={i > 0} setStepRef={setStepRef} />;
      $[4] = setStepRef;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    t2 = steps.map(t3);
    $[0] = setStepRef;
    $[1] = state.visibleSteps;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  let t3;
  if ($[6] !== t1 || $[7] !== t2) {
    t3 = <div className={t1}>{t2}</div>;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
}

/**
 * Recursive step renderer — handles nested decision branches by recursing
 * on each branch step. Mirrors the structure used at the top level so a
 * Decision sitting inside another Decision's branch still renders its own
 * branches and children.
 */
function _temp2(s) {
  return !isLinkedMessage(s);
}
function SnapshotStep(t0) {
  const $ = _c(15);
  const {
    step,
    showConnector,
    setStepRef
  } = t0;
  let t1;
  if ($[0] !== setStepRef || $[1] !== showConnector || $[2] !== step) {
    const branches = step.branches ?? [];
    const isDecisionWithBranches = step.type === StepType.DECISION && branches.length > 0;
    let t2;
    if ($[4] !== setStepRef || $[5] !== step.id) {
      t2 = setStepRef?.(step.id);
      $[4] = setStepRef;
      $[5] = step.id;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    let t3;
    if ($[7] !== showConnector || $[8] !== step.minutesAfterPrevious) {
      t3 = showConnector && <DateConnector minutesGap={step.minutesAfterPrevious} />;
      $[7] = showConnector;
      $[8] = step.minutesAfterPrevious;
      $[9] = t3;
    } else {
      t3 = $[9];
    }
    let t4;
    if ($[10] !== step) {
      t4 = <StepCard step={step} />;
      $[10] = step;
      $[11] = t4;
    } else {
      t4 = $[11];
    }
    let t5;
    if ($[12] !== step.id || $[13] !== t4) {
      t5 = <DiffStepWrapper stepId={step.id}>{t4}</DiffStepWrapper>;
      $[12] = step.id;
      $[13] = t4;
      $[14] = t5;
    } else {
      t5 = $[14];
    }
    t1 = <div ref={t2} className="flex flex-col items-center p-1">{t3}{t5}{isDecisionWithBranches && <DecisionStep.Root step={step}><DecisionStep.BranchDisplay>{[...branches].sort(_temp3).map(_temp6)}</DecisionStep.BranchDisplay></DecisionStep.Root>}</div>;
    $[0] = setStepRef;
    $[1] = showConnector;
    $[2] = step;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}

// ============================================================================
// Snapshot header
// ============================================================================
function _temp6(branch) {
  const visibleChildren = branch.steps.filter(_temp4);
  return <DecisionStep.Branch key={branch.id} branch={branch}><DecisionStep.BranchLabel /><div className="flex flex-col items-center gap-2">{visibleChildren.map(_temp5)}</div></DecisionStep.Branch>;
}
function _temp5(branchStep, j) {
  return <SnapshotStep key={branchStep.id} step={branchStep} showConnector={j > 0} />;
}
function _temp4(s) {
  return !isLinkedMessage(s);
}
function _temp3(a, b) {
  return a.position - b.position;
}
function SnapshotHeader(t0) {
  const $ = _c(21);
  const {
    snapshot,
    carePlanChanges
  } = t0;
  let t1;
  if ($[0] !== carePlanChanges) {
    t1 = carePlanChanges.find(_temp7);
    $[0] = carePlanChanges;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const nameChange = t1;
  const startDateChange = carePlanChanges.find(_temp8);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      ...carePlanTagQueryOptions.list()
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    data: allTags
  } = useQuery(t2);
  const t3 = "rounded-lg border border-border bg-background-secondary p-3";
  let t4;
  if ($[3] !== nameChange || $[4] !== snapshot.name) {
    t4 = <div className="flex items-center gap-2">{nameChange ? <h4 className="text-body-strong text-foreground">{nameChange.oldValue && <span className="mr-2 text-foreground-secondary line-through">{nameChange.oldValue}</span>}<span className="text-status-success-text">{nameChange.newValue ?? snapshot.name ?? "Untitled"}</span></h4> : <h4 className="text-body-strong text-foreground">{snapshot.name ?? "Untitled Care Plan"}</h4>}</div>;
    $[3] = nameChange;
    $[4] = snapshot.name;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const t5 = (snapshot.startDate || startDateChange) && <p className="mt-0.5 text-caption text-foreground-secondary">{startDateChange ? <>{startDateChange.oldValue && <span className="mr-1 line-through">{formatDate(startDateChange.oldValue)}</span>}{startDateChange.oldValue && startDateChange.newValue && <span className="mr-1">→</span>}{startDateChange.newValue && <span className="text-status-success-text">{formatDate(startDateChange.newValue)}</span>}</> : <span>Started {formatDate(snapshot.startDate)}</span>}</p>;
  let t6;
  if ($[6] !== allTags || $[7] !== snapshot.tagIds) {
    t6 = snapshot.tagIds.length > 0 && <div className="mt-1"><CarePlanTagDisplay tags={allTags?.filter(t => snapshot.tagIds.includes(t.id)) ?? []} /></div>;
    $[6] = allTags;
    $[7] = snapshot.tagIds;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== snapshot.displaySteps) {
    t7 = snapshot.displaySteps.filter(_temp9);
    $[9] = snapshot.displaySteps;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t7.length) {
    t8 = <p className="mt-1 text-caption text-foreground-secondary">{t7.length} steps</p>;
    $[11] = t7.length;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== carePlanChanges) {
    t9 = <CarePlanLevelChanges carePlanChanges={carePlanChanges} />;
    $[13] = carePlanChanges;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== t4 || $[16] !== t5 || $[17] !== t6 || $[18] !== t8 || $[19] !== t9) {
    t10 = <div className={t3}>{t4}{t5}{t6}{t8}{t9}</div>;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
    $[18] = t8;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  return t10;
}
function _temp9(s) {
  return !isLinkedMessage(s);
}
function _temp8(c_0) {
  return c_0.fieldName === "START_DATE";
}
function _temp7(c) {
  return c.fieldName === "NAME";
}
function CarePlanLevelChanges(t0) {
  const $ = _c(7);
  const {
    carePlanChanges
  } = t0;
  let t1;
  let t2;
  let t3;
  if ($[0] !== carePlanChanges) {
    t3 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const otherChanges = carePlanChanges.filter(_temp10);
      if (otherChanges.length === 0) {
        t3 = null;
        break bb0;
      }
      t1 = "mt-2 space-y-0.5 border-t border-border pt-2";
      t2 = otherChanges.map(_temp11);
    }
    $[0] = carePlanChanges;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
  } else {
    t1 = $[1];
    t2 = $[2];
    t3 = $[3];
  }
  if (t3 !== Symbol.for("react.early_return_sentinel")) {
    return t3;
  }
  let t4;
  if ($[4] !== t1 || $[5] !== t2) {
    t4 = <div className={t1}>{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
}
function _temp11(change, i) {
  return <div key={`${change.fieldName}-${i}`} className="flex items-center gap-1.5 text-caption"><span className="font-medium text-foreground-secondary">{formatToHumanReadable(change.fieldName)}:</span>{change.oldValue && <span className="text-foreground-secondary line-through">{change.oldValue}</span>}{change.oldValue && change.newValue && <span className="text-foreground-muted">→</span>}{change.newValue && <span className="text-foreground">{change.newValue}</span>}</div>;
}
function _temp10(c) {
  return c.fieldName !== "NAME" && c.fieldName !== "START_DATE" && c.fieldName !== "TAG_IDS";
}